import { useCallback, useState } from 'react';
import { ClientFeatureUsageInfo, _PlanType } from '../clientContracts/plan/planDTO';
import { planApi } from '../services/planApi';
import { FeatureState } from '../types/featureUsage';
import { UsageInfo } from '../types/usage';

const INITIAL_STATE: FeatureState = {
    featureUsages: [],
    usages: [],
    clientPlan: undefined,
    plans: [],
    isLoading: false,
    error: null,
    clientPlanWithUsage: undefined,
    accountManagers: [],
};

export const useFeatureUsageData = () => {
    const [state, setState] = useState<FeatureState>(INITIAL_STATE);

    const setLoading = useCallback((isLoading: boolean) => {
        setState((prev) => ({ ...prev, isLoading, error: null }));
    }, []);

    const handleError = useCallback((error: unknown) => {
        const normalizedError =
            error instanceof Error ? error : new Error('Unknown error occurred');
        setState((prev) => ({ ...prev, isLoading: false, error: normalizedError }));
        return normalizedError;
    }, []);

    const mapUsageInfo = useCallback(
        (usage: ClientFeatureUsageInfo, planType: _PlanType): UsageInfo => ({
            planType,
            feature: usage.feature,
            usage: usage.usage,
            usageCap: usage.usageCap ?? 0,
            usageInPercent: Math.round((usage.usage / (usage.usageCap ?? 1)) * 100),
        }),
        [],
    );
    const refreshFeatureUsage = useCallback(async () => {
        setLoading(true);
        try {
            const [clientPlanWithUsageResponse, featureUsagesResponse] = await Promise.all([
                planApi.getClientPlanWithUsage(),
                planApi.getFeatureUsage(),
            ]);

            setState((prev) => ({
                ...prev,
                clientPlanWithUsage: clientPlanWithUsageResponse.payload,
                featureUsages: featureUsagesResponse.payload,
            }));
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError]);

    const fetchAllFeatureUsage = useCallback(async () => {
        setLoading(true);

        try {
            const [
                clientPlanResponse,
                plansResponse,
                featureUsagesResponse,
                clientPlanWithUsageResponse,
                accountManagersResponse,
            ] = await Promise.all([
                planApi.getClientPlan(),
                planApi.getAllPlans(),
                planApi.getFeatureUsage(),
                planApi.getClientPlanWithUsage(),
                planApi.getAccountManagers(),
            ]);

            const currentPlan = clientPlanResponse.success ? clientPlanResponse.payload : undefined;
            const plans = plansResponse.success ? plansResponse.payload : [];
            const featureUsages = featureUsagesResponse.success
                ? featureUsagesResponse.payload
                : [];

            if (!currentPlan) {
                return setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    clientPlan: undefined,
                }));
            }

            if (!featureUsages || !plans.length) {
                throw new Error('Failed to fetch required data');
            }

            const validFeatureUsages = featureUsages.filter(Boolean);
            const newUsages = validFeatureUsages.map((usage) =>
                mapUsageInfo(usage, currentPlan.plan.planType),
            );

            setState((prev) => ({
                ...prev,
                featureUsages: validFeatureUsages,
                usages: newUsages,
                clientPlan: currentPlan,
                plans,
                isLoading: false,
                clientPlanWithUsage: clientPlanWithUsageResponse.payload,
                accountManagers: accountManagersResponse.payload,
            }));
        } catch (error) {
            handleError(error);
        }
    }, [mapUsageInfo, setLoading, handleError]);

    const startPlan = useCallback(
        async (planId: number, billingPeriod: string) => {
            setLoading(true);

            try {
                const response = await planApi.startPlan(planId, billingPeriod);
                setLoading(false);
                return response;
            } catch (error) {
                throw handleError(error);
            }
        },
        [setLoading, handleError],
    );
    const updateClientPlan = useCallback(async () => {
        setLoading(true);
        try {
            const response = await planApi.getClientPlan();
            setState((prev) => ({ ...prev, clientPlan: response.payload }));
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError]);
    const updateFeatureUsage = useCallback(async () => {
        setLoading(true);
        try {
            const response = await planApi.getFeatureUsage();
            const validFeatureUsages = response.payload.filter(Boolean);
            const newUsages = validFeatureUsages.map((usage) =>
                mapUsageInfo(usage, state?.clientPlan?.plan?.planType ?? 'BASIC'),
            );

            setState((prev) => ({ ...prev, usages: newUsages }));
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, mapUsageInfo, state.clientPlan?.plan.planType, handleError]);

    return {
        ...state,
        fetchAllFeatureUsage,
        refreshFeatureUsage,
        fetchAllPlans: planApi.getAllPlans,
        startPlan,
        fetchClientPlanWithUsage: planApi.getClientPlanWithUsage,
        fetchAccountManagers: planApi.getAccountManagers,
        updateClientPlan,
        updateFeatureUsage,
    };
};
